// ════════════════════════════════════════════════════════════════════════════
// Finding persisted attach blocks in stored content, and judging whether an
// edit left them alone.
//
// §7.3 rules the enforcement as MECHANISM, not copy: a patch may remove a
// block whole, and may not alter one's interior. The copy inside the block
// says the same thing to a model reading it, but the copy is not what stops
// the edit — this module is.
//
// Everything here is pure and reads only strings. It never parses the block's
// items (that is `parse-attach-document.ts`'s job): the integrity question is
// about BYTES, and a body this module could not read must still be protected.
// ════════════════════════════════════════════════════════════════════════════

import {
  SENTINEL_CAPTION_PREFIX,
  SENTINEL_CAPTION_SUFFIX,
} from './attached-result-constants.js';
import { fenceSpansOfKind, type FenceSpan } from './fence-spans.js';
import { ATTACH_FENCE_INFO_STRING } from './grammar.js';

/** One located block. `interior` is the body verbatim — the bytes the
 *  integrity rule compares — and the parsed parts are conveniences over it.
 *
 *  There is no payload sub-region any more: the START/END markers that used to
 *  bound one are gone (ruled 2026-08-19), and the fence carries the bound they
 *  marked. What is inside the fence IS the block. */
export interface SentinelBlock {
  readonly span: FenceSpan;
  readonly interior: string;
  /** The whole caption line, or null when the block carries none. */
  readonly captionLine: string | null;
  /** The datetime the caption states, as printed. Null when the caption is
   *  absent or does not carry the ruled frame. */
  readonly capturedAtLabel: string | null;
}

/** Why an edit is refused. One member per ruled failure, so the caller's copy
 *  is chosen by the mechanism rather than by a string comparison. */
export enum SentinelIntegrityViolationKind {
  /** A sentinel in the edited body carries an interior no sentinel in the
   *  stored body carried: an interior edit, or a hand-written block. */
  InteriorAltered = 'interior_altered',
  /** A sentinel's caption survived the edit outside any sentinel block: the
   *  block was un-fenced rather than removed, leaving its data loose in the
   *  document with nothing marking it as data. */
  BlockUnfenced = 'block_unfenced',
}

export interface SentinelIntegrityViolation {
  readonly kind: SentinelIntegrityViolationKind;
  /** The capture datetime the offending block states, when it states one —
   *  the one part of a sentinel a reader can use to tell two of them apart. */
  readonly capturedAtLabel: string | null;
}

function captionLineOf(input: { interior: string }): string | null {
  return (
    input.interior
      .split('\n')
      .map((line) => line.trim())
      .find(
        (line) =>
          line.startsWith(SENTINEL_CAPTION_PREFIX) &&
          line.endsWith(SENTINEL_CAPTION_SUFFIX),
      ) ?? null
  );
}

function capturedAtLabelOf(input: { captionLine: string | null }): string | null {
  if (input.captionLine === null) return null;
  const label = input.captionLine.slice(
    SENTINEL_CAPTION_PREFIX.length,
    input.captionLine.length - SENTINEL_CAPTION_SUFFIX.length,
  );
  return label.length > 0 ? label : null;
}

/** Every persisted attach block in a document, in document order. */
export function detectSentinelBlocks(input: { markdown: string }): SentinelBlock[] {
  return fenceSpansOfKind({
    markdown: input.markdown,
    info: ATTACH_FENCE_INFO_STRING,
  }).map((span) => {
    const captionLine = captionLineOf({ interior: span.body });
    return {
      span,
      interior: span.body,
      captionLine,
      capturedAtLabel: capturedAtLabelOf({ captionLine }),
    };
  });
}

/**
 * What an edit did to the sentinels.
 *
 * Byte comparison of interiors, in both directions, which is what makes the
 * ruled pair of behaviours fall out of ONE rule rather than out of span
 * arithmetic against an anchor:
 *
 *   whole-block removal  the edited body simply holds one sentinel fewer, and
 *                        nothing it holds is unaccounted for → allowed.
 *   interior edit        the edited body holds a sentinel whose interior no
 *                        stored sentinel had → refused.
 *   forgery              same shape, same refusal: a hand-written block's
 *                        interior matches nothing stored, and a model may not
 *                        author verified data.
 *   un-fencing           the block's own caption survives outside any block →
 *                        refused. Removal means the data goes, not that it is
 *                        left loose with its fence taken off.
 *
 * Editing everywhere ELSE in the document is untouched by all of this: an edit
 * that does not overlap a sentinel changes no interior, so it produces no
 * violation.
 */
export function assessSentinelIntegrity(input: {
  before: string;
  after: string;
}): SentinelIntegrityViolation[] {
  const before = detectSentinelBlocks({ markdown: input.before });
  const after = detectSentinelBlocks({ markdown: input.after });
  const storedInteriors = new Set(before.map((block) => block.interior));

  const altered = after
    .filter((block) => !storedInteriors.has(block.interior))
    .map((block) => ({
      kind: SentinelIntegrityViolationKind.InteriorAltered,
      capturedAtLabel: block.capturedAtLabel,
    }));

  const survivingInteriors = new Set(after.map((block) => block.interior));
  const unfenced = before
    .filter((block) => !survivingInteriors.has(block.interior))
    .filter((block) => block.captionLine !== null)
    .filter((block) => captionSurvivesLoose({ after: input.after, block, blocks: after }))
    .map((block) => ({
      kind: SentinelIntegrityViolationKind.BlockUnfenced,
      capturedAtLabel: block.capturedAtLabel,
    }));

  return [...altered, ...unfenced];
}

/** True when the removed block's caption is still in the document at a
 *  position no surviving sentinel covers. Our own caption text is specific
 *  enough that its presence outside a block means one was un-fenced, never
 *  that someone happened to write the same sentence. */
function captionSurvivesLoose(input: {
  after: string;
  block: SentinelBlock;
  blocks: readonly SentinelBlock[];
}): boolean {
  const caption = input.block.captionLine;
  if (caption === null) return false;
  const at = input.after.indexOf(caption);
  if (at === -1) return false;
  return !input.blocks.some(
    (surviving) => at >= surviving.span.start && at < surviving.span.end,
  );
}
